import { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

export default function StaffActivitiesPage() {
  const today = new Date().toISOString().slice(0, 10);
  const [staffOptions, setStaffOptions] = useState([]);
  const [activities, setActivities] = useState([]);
  const [filters, setFilters] = useState({ activity_date: '', staff_id: 'all' });
  const [form, setForm] = useState({ staff_id: '', activity_date: today, activity: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchStaff();
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [filters]);

  const fetchStaff = async () => {
    try {
      const response = await adminApi.getSalesStaff();
      setStaffOptions(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch staff');
    }
  };

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.activity_date) params.activity_date = filters.activity_date;
      if (filters.staff_id && filters.staff_id !== 'all') params.staff_id = filters.staff_id;
      const response = await adminApi.getStaffActivities(params);
      setActivities(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch activities');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.staff_id || !form.activity_date || !form.activity.trim()) {
      toast.error('Staff, date and activity are required');
      return;
    }
    setSaving(true);
    try {
      await adminApi.createStaffActivity({
        staff_id: form.staff_id,
        activity_date: form.activity_date,
        activity: form.activity.trim(),
      });
      toast.success('Activity added');
      setForm({ staff_id: '', activity_date: today, activity: '' });
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add activity');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="staff-activities-page">
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Staff Activities</h1>
        <p className="text-[#A8A8A8] mt-1">Track daily activities for staff</p>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Log Activity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Staff *</Label>
              <Select value={form.staff_id} onValueChange={(v) => setForm({ ...form, staff_id: v })}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select staff" />
                </SelectTrigger>
                <SelectContent>
                  {staffOptions.map((staff) => (
                    <SelectItem key={staff.id} value={staff.id}>{staff.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Date *</Label>
              <Input
                type="date"
                value={form.activity_date}
                onChange={(e) => setForm({ ...form, activity_date: e.target.value })}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Activity *</Label>
              <Input
                value={form.activity}
                onChange={(e) => setForm({ ...form, activity: e.target.value })}
                placeholder="Enter activity"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleCreate}
              disabled={saving}
              className="bg-[#C97A5E] hover:bg-[#B06850]"
              data-testid="save-staff-activity"
            >
              {saving ? 'Saving...' : 'Add Activity'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Activities</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Filter by Date</Label>
              <Input
                type="date"
                value={filters.activity_date}
                onChange={(e) => setFilters({ ...filters, activity_date: e.target.value })}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Filter by Staff</Label>
              <Select
                value={filters.staff_id}
                onValueChange={(v) => setFilters({ ...filters, staff_id: v })}
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="All staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staffOptions.map((staff) => (
                    <SelectItem key={staff.id} value={staff.id}>{staff.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-[#A8A8A8]">Loading...</p>
          ) : activities.length === 0 ? (
            <p className="text-sm text-[#A8A8A8]">No activities found.</p>
          ) : (
            <div className="divide-y divide-[#E5E5E5]">
              {activities.map((activity) => (
                <div key={activity.id} className="py-3">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-[#2D2420]">{activity.staff_name}</p>
                    <p className="text-sm text-[#A8A8A8]">{activity.activity_date}</p>
                  </div>
                  <p className="text-sm text-[#5C4033]">{activity.activity}</p>
                  {activity.created_by_name ? (
                    <p className="text-xs text-[#A8A8A8]">Logged by {activity.created_by_name}</p>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
